import { createPortal } from "react-dom";
import { useEffect, useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { X } from "lucide-react";
import { useOneShotModal } from "./useOneShotModal";

/**
 * One-shot confirm dialog — a peer primitive to `CoachModal`, not
 * a wrapper. Replaces every `window.confirm()` in the app per the
 * no-native-browser-chrome rule.
 *
 * Used by:
 *   - Coach Review: clear-drawing, template-overwrite,
 *     Apply-Formation (M7.5 B4 / B5 / B6).
 *   - Future: any other destructive or replace-style action that
 *     used to use `window.confirm`.
 *
 * Sized smaller than `CoachModal` (max-w-sm, no scrollable body)
 * and built with a single button row (confirm + cancel) — the
 * form-modal shape doesn't fit a yes/no prompt. The two primitives
 * share `useOneShotModal` for Escape handling; the M6a `CoachModal`
 * gets the same hook by adoption if/when a polish pass touches it.
 *
 * `danger` flips the confirm button to a destructive variant —
 * use it for clear-drawing / Apply-Formation (the legacy
 * `confirmAction({ danger: true })` flag).
 */

export type ConfirmDialogProps = {
  /** Heading + accessible name. */
  title: string;
  /** Body copy. Can be a string or rich content. */
  message: ReactNode;
  /** Confirm-button label. */
  confirmLabel?: string;
  /** Cancel-button label. */
  cancelLabel?: string | null;
  /** Use the approved wide dialog composition for dense grids/details. */
  wide?: boolean;
  /** `true` ⇒ destructive style on the confirm button. */
  danger?: boolean;
  /** Disable confirm while an async caller is already submitting. */
  confirmDisabled?: boolean;
  /** Optional first focus target for reference-matched dialog variants. */
  initialFocus?: "close" | "cancel" | "confirm";
  /** Called when the user clicks confirm. */
  onConfirm: () => void;
  /** Called when the user clicks cancel / backdrop / Escape. */
  onClose: () => void;
  /**
   * Optional THIRD choice, for the "save / discard / cancel" shape where
   * cancelling and discarding are different outcomes. Rendered between
   * cancel and confirm, and joined to the Tab cycle.
   *
   * Omit it and the dialog stays a plain two-button confirm.
   */
  altAction?: {
    label: string;
    onClick: () => void;
    /** `true` ⇒ destructive style (e.g. "Discard changes"). */
    danger?: boolean;
    "data-testid"?: string;
  };
  /** Optional `data-testid` for the dialog element. */
  "data-testid"?: string;
  /** Optional test id for the primary action when a domain flow needs one. */
  confirmTestId?: string;
};

export function ConfirmDialog({
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  wide = false,
  danger = false,
  confirmDisabled = false,
  initialFocus,
  onConfirm,
  onClose,
  altAction,
  confirmTestId,
  ...rest
}: ConfirmDialogProps) {
  useOneShotModal(onClose);

  // M7.5 review-fix P2: focus management for the alertdialog. On
  // mount, capture the previously focused element so we can restore
  // it on unmount, then move focus into the dialog (Cancel when
  // `danger`, Confirm otherwise — the safer default for destructive
  // actions). Tab is trapped between the two buttons.
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Capture before portal children with autoFocus can replace the trigger.
  const previousFocusRef = useRef<HTMLElement | null>(
    typeof document === "undefined"
      ? null
      : (document.activeElement as HTMLElement | null),
  );
  useEffect(() => {
    const prev = previousFocusRef.current;
    const initial =
      initialFocus === "close"
        ? closeBtnRef.current
        : initialFocus === "cancel"
          ? cancelBtnRef.current
          : initialFocus === "confirm"
            ? confirmBtnRef.current
            : danger
              ? cancelBtnRef.current
              : confirmBtnRef.current;
    initial?.focus();
    return () => {
      try {
        prev?.focus();
      } catch {
        /* trigger may have been removed; ignore */
      }
    };
    // Only set up once per mount; reading the initial focus policy once is
    // intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trap Tab inside the dialog so background controls can't be
  // reached while the alertdialog is open.
  const altBtnRef = useRef<HTMLButtonElement | null>(null);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    if (
      ["approved-v5", "platform"].includes(
        document.documentElement.dataset.ui ?? "",
      )
    ) {
      // Some existing confirmations contain forms. Keep every enabled field
      // reachable, and leave a portaled picker's own keyboard handler in charge.
      if (!panelRef.current?.contains(e.target as Node)) return;
      const fields = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]',
        ),
      ).filter((el) => el.getClientRects().length && !el.closest("[hidden]"));
      const first = fields[0],
        last = fields.at(-1);
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
      return;
    }
    // Cycle over whichever buttons are actually rendered, so adding the
    // optional third action doesn't strand it outside the trap.
    const order = [
      cancelBtnRef.current,
      altBtnRef.current,
      confirmBtnRef.current,
    ].filter((el): el is HTMLButtonElement => el != null);
    if (order.length < 2) return;
    const index = order.indexOf(document.activeElement as HTMLButtonElement);
    if (index === -1) return;
    const next = e.shiftKey
      ? order[(index - 1 + order.length) % order.length]
      : order[(index + 1) % order.length];
    e.preventDefault();
    next.focus();
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[140] flex items-end justify-center bg-black/80 p-3 sm:items-center sm:p-4"
      role="alertdialog"
      aria-modal="true"
      aria-label={title}
      data-testid={rest["data-testid"]}
      data-confirm-dialog="true"
      data-confirm-wide={wide ? "true" : undefined}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={onKeyDown}
    >
      <div
        ref={panelRef}
        data-confirm-panel
        className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-sm flex-col gap-3 overflow-y-auto rounded border border-rule bg-surface p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:p-5"
      >
        <h2 className="text-base font-display tracking-tight text-ink">
          {title}
        </h2>
        <button
          ref={closeBtnRef}
          type="button"
          data-confirm-close
          aria-label="Close dialog"
          onClick={onClose}
        >
          <X aria-hidden />
        </button>
        <div data-confirm-body className="text-sm text-ink-muted">
          {message}
        </div>
        <div
          data-confirm-actions
          className="mt-1 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end"
        >
          {cancelLabel ? (
            <button
              ref={cancelBtnRef}
              type="button"
              onClick={onClose}
              data-testid="confirm-dialog-cancel"
              className="rounded border border-rule bg-canvas px-3 py-1.5 text-sm font-display uppercase tracking-wide text-ink transition-colors hover:bg-field focus:outline-none focus:ring-2 focus:ring-accent"
            >
              {cancelLabel}
            </button>
          ) : null}
          {altAction ? (
            <button
              ref={altBtnRef}
              type="button"
              onClick={altAction.onClick}
              data-testid={altAction["data-testid"] ?? "confirm-dialog-alt"}
              className={
                altAction.danger
                  ? "rounded border border-destructive bg-canvas px-3 py-1.5 text-sm font-display uppercase tracking-wide text-destructive transition-colors hover:bg-destructive hover:text-accent-ink focus:outline-none focus:ring-2 focus:ring-destructive"
                  : "rounded border border-rule bg-canvas px-3 py-1.5 text-sm font-display uppercase tracking-wide text-ink transition-colors hover:bg-field focus:outline-none focus:ring-2 focus:ring-accent"
              }
            >
              {altAction.label}
            </button>
          ) : null}
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={() => {
              if (!confirmDisabled) onConfirm();
            }}
            disabled={confirmDisabled}
            data-testid={confirmTestId ?? "confirm-dialog-confirm"}
            className={[
              "rounded px-3 py-1.5 text-sm font-display uppercase tracking-wide transition-colors focus:outline-none focus:ring-2 focus:ring-accent",
              danger
                ? "bg-destructive text-paper hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                : "bg-accent text-accent-ink hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60",
            ].join(" ")}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
